import React from 'react';
import { Button, DatePicker, Input, List, Select } from 'antd';
import type { Dispatch, SetStateAction } from 'react';
import locale from 'antd/es/date-picker/locale/zh_CN';
import moment from 'moment';
import 'moment/locale/zh-cn';
import type { DockingFilter, DockingStatistics, ProjectDockingSummary } from '../../types/docking';
import { projectCategoryLabel } from '../../utils/marketHelpers';

export interface DockingSidebarProps {
  statistics: DockingStatistics;
  projectSummaries: ProjectDockingSummary[];
  filter: DockingFilter;
  setFilter: Dispatch<SetStateAction<DockingFilter>>;
  selectedProjectId: string | null;
  onSelectProject: (projectId: string) => void;
  onReselectSameProject?: (projectId: string) => void;
}

export function DockingSidebar(props: DockingSidebarProps): React.ReactElement {
  const {
    statistics,
    projectSummaries,
    filter,
    setFilter,
    selectedProjectId,
    onSelectProject,
    onReselectSameProject,
  } = props;

  const dateFrom = filter.dateFrom ? moment(filter.dateFrom) : null;
  const dateTo = filter.dateTo ? moment(filter.dateTo) : null;

  return (
    <aside className="dash-panel dash-panel--left">
      <div className="dash-section">
        <div className="dash-stat-grid dash-stat-grid--market">
          <div className="dash-stat-card">
            <div className="dash-stat-card__label">已对接前期项目</div>
            <div className="dash-stat-card__value dash-stat-card__value--preliminary">
              {statistics.preliminary}
            </div>
          </div>
          <div className="dash-stat-card">
            <div className="dash-stat-card__label">已对接履约项目</div>
            <div className="dash-stat-card__value dash-stat-card__value--performance">
              {statistics.performance}
            </div>
          </div>
        </div>
      </div>

      <div className="dash-section">
        <div className="dash-field-label">项目搜索</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Input
            allowClear
            value={filter.keyword ?? ''}
            placeholder="请输入项目关键字"
            onChange={(e) =>
              setFilter((prev) => ({
                ...prev,
                keyword: e.target.value.trim() ? e.target.value : undefined,
              }))
            }
          />
          <Button
            type="default"
            disabled={!filter.keyword?.trim()}
            onClick={() => setFilter((prev) => ({ ...prev, keyword: undefined }))}
          >
            清空
          </Button>
        </div>

        <div className="dash-field-label dash-field-label--spaced">项目类别</div>
        <Select
          className="dash-select"
          dropdownClassName="dash-select-dropdown"
          style={{ width: '100%' }}
          value={filter.category}
          onChange={(v) => setFilter((prev) => ({ ...prev, category: v }))}
          options={[
            { label: '全部', value: 'all' },
            { label: '前期项目', value: 'preliminary' },
            { label: '履约项目', value: 'performance' },
          ]}
        />

        <div className="dash-field-label dash-field-label--spaced">对接时间范围</div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <DatePicker
            locale={locale}
            placeholder="开始日期"
            allowClear
            style={{ flex: 1 }}
            value={dateFrom}
            onChange={(d) =>
              setFilter((prev) => ({
                ...prev,
                dateFrom: d ? d.format('YYYY-MM-DD') : undefined,
              }))
            }
          />
          <span style={{ color: 'rgba(255,255,255,0.45)' }}>~</span>
          <DatePicker
            locale={locale}
            placeholder="结束日期"
            allowClear
            style={{ flex: 1 }}
            value={dateTo}
            onChange={(d) =>
              setFilter((prev) => ({
                ...prev,
                dateTo: d ? d.format('YYYY-MM-DD') : undefined,
              }))
            }
          />
        </div>

        <div className="dash-field-label dash-field-label--spaced">项目成熟度</div>
        <Select
          allowClear
          placeholder="请选择"
          className="dash-select"
          dropdownClassName="dash-select-dropdown"
          style={{ width: '100%' }}
          value={filter.maturity ?? undefined}
          onChange={(v) => setFilter((prev) => ({ ...prev, maturity: v ?? null }))}
          options={[
            { label: '1 星', value: 1 },
            { label: '2 星', value: 2 },
            { label: '3 星', value: 3 },
            { label: '4 星', value: 4 },
            { label: '5 星', value: 5 },
          ]}
        />
      </div>

      <div className="dash-section dash-section--grow">
        <div className="dash-field-label">
          已对接项目列表（{projectSummaries.length} 个项目）
        </div>
        <div className="dash-list-wrap">
          <List
            size="small"
            dataSource={projectSummaries}
            locale={{ emptyText: '暂无对接记录' }}
            renderItem={(item) => {
              const isSelected = selectedProjectId === item.projectId;
              const categoryClass =
                item.project.category === 'preliminary'
                  ? 'dash-list-item--preliminary'
                  : 'dash-list-item--performance';
              return (
                <List.Item
                  className={
                    isSelected
                      ? `dash-list-item dash-list-item--active ${categoryClass}`
                      : `dash-list-item ${categoryClass}`
                  }
                  onClick={() => {
                    if (selectedProjectId === item.projectId) {
                      onReselectSameProject?.(item.projectId);
                    }
                    onSelectProject(item.projectId);
                  }}
                >
                  <div className="dash-list-item__stack">
                    <div className="dash-list-item__title-row">
                      <span className="dash-list-item__name">{item.project.name}</span>
                      <span className="dash-list-item__badge">{item.recordCount} 次</span>
                    </div>
                    <span className="dash-list-item__meta">
                      {projectCategoryLabel(item.project.category)} · 最近 {item.latestDockedAt}
                      {item.latestCounterpartyOrg
                        ? ` · ${item.latestCounterpartyOrg}`
                        : ''}
                    </span>
                  </div>
                </List.Item>
              );
            }}
          />
        </div>
      </div>
    </aside>
  );
}
